import React, {useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import {lookupBarcode} from '../../offers/cors/_requests'
import {PackageChild, PackageVariant, productActionType, productState} from '../cors/reducers'

type Props = {
  state: productState
  dispatch: React.Dispatch<any>
  token?: string
  language: string
}

/**
 * "Create package" section on the add-single-product form. Turns the new
 * product into a package (is_offer) built from the supplier's OWN products.
 *
 * The supplier picks child barcodes + quantities; each barcode is validated
 * client-side against the offers lookup (state 'already_yours' = the supplier
 * owns an active offer on it). The backend re-validates ownership and computes
 * the authoritative package price from the components at submit time, so this
 * section deliberately does NOT price the package itself.
 */
const PackageSection: React.FC<Props> = ({state, dispatch, token, language}) => {
  const {formatMessage} = useIntl()

  const createPackage = !!state.createPackage
  const children: PackageChild[] = state.packageChildren ?? []
  const [checking, setChecking] = useState<Record<number, boolean>>({})

  const t = (id: string, defaultMessage: string) => formatMessage({id, defaultMessage})

  const update = (patch: Partial<productState>) =>
    dispatch({type: productActionType.setPackage, payload: patch})

  const setChildren = (next: PackageChild[]) => update({packageChildren: next})

  const toggle = () => {
    const turningOn = !createPackage
    update({
      createPackage: turningOn,
      packageChildren: turningOn && children.length === 0 ? [{barcode: '', quantity: 1}] : children,
    })
  }

  const addRow = () => setChildren([...children, {barcode: '', quantity: 1}])
  const removeRow = (i: number) => setChildren(children.filter((_, idx) => idx !== i))
  const setRow = (i: number, patch: Partial<PackageChild>) =>
    setChildren(children.map((c, idx) => (idx === i ? {...c, ...patch} : c)))

  const clearResolution: Partial<PackageChild> = {
    valid: undefined,
    name: undefined,
    price: undefined,
    product_id: undefined,
    variants: undefined,
  }

  const resolveBarcode = async (i: number, barcode: string) => {
    const bc = barcode.trim()
    if (!bc) {
      setRow(i, clearResolution)
      return
    }
    setChecking((s) => ({...s, [i]: true}))
    try {
      const res = await lookupBarcode(bc, token, language)
      const data: any = res?.data
      if (data?.state === 'already_yours' && data?.product) {
        // Single owned product — resolved directly to its product_id.
        setRow(i, {
          ...clearResolution,
          valid: true,
          name: data.product.name ?? bc,
          price: typeof data.price === 'number' ? data.price : undefined,
          product_id: Number(data.product.product_id) || undefined,
        })
      } else if (data?.state === 'multiple_match' && Array.isArray(data?.products)) {
        // Barcode has size/color variants — the supplier must pick which one.
        // Only their OWN active variants are selectable.
        const owned: PackageVariant[] = data.products
          .filter((p: any) => p.has_my_offer && p.my_offer_active)
          .map((p: any) => ({
            product_id: Number(p.product_id),
            price: typeof p.price === 'number' ? p.price : 0,
            active: true,
            label:
              [p.size_label, p.color_label].filter(Boolean).join(' / ') ||
              (p.name ?? String(p.product_id)),
          }))
        setRow(i, owned.length === 0 ? {...clearResolution, valid: false} : {...clearResolution, variants: owned})
      } else {
        setRow(i, {...clearResolution, valid: false})
      }
    } catch {
      setRow(i, {...clearResolution, valid: false})
    } finally {
      setChecking((s) => ({...s, [i]: false}))
    }
  }

  const pickVariant = (i: number, v: PackageVariant) =>
    setRow(i, {valid: true, name: v.label, price: v.price, product_id: v.product_id})

  const filled = children.filter((c) => c.barcode.trim())
  const kindLabel =
    filled.length === 1
      ? t('pkg_kind_multipack', 'Multipack — same product ×N')
      : filled.length > 1
      ? t('pkg_kind_bundle', 'Bundle — multiple products')
      : ''

  // Package price is derived from the components — the supplier never types it.
  // base = sum(child price x qty); there is no supplier discount on a package.
  const allPriced = filled.length > 0 && filled.every((c) => typeof c.price === 'number' && c.valid)
  const base = allPriced ? filled.reduce((s, c) => s + (c.price || 0) * (c.quantity || 1), 0) : null
  const effective = base

  // Push the computed Sale Price + a nominal Quantity into the product form so
  // the Product Details step is auto-filled (and read-only) for a package. The
  // backend recomputes the authoritative price at submit; this is the preview.
  useEffect(() => {
    if (!createPackage) return
    const priced = effective != null
    dispatch({
      type: productActionType.setPackage,
      payload: {
        packageAutoPriced: priced,
        quantity: {error: false, value: '1'},
        // Only overwrite Sale Price once we actually computed it; otherwise
        // leave it editable so the flow isn't blocked if a child price is
        // unavailable (e.g. backend not yet returning it).
        ...(priced ? {salePrice: {error: false, value: effective!.toFixed(2)}} : {}),
      },
    })
  }, [createPackage, effective]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className='aps-package card mt-4' style={{border: '1px solid #E4E6EF', borderRadius: 12}}>
      <div className='card-body'>
        <label className='form-check form-switch form-check-custom form-check-solid d-flex align-items-center gap-3 mb-0'>
          <input
            className='form-check-input'
            type='checkbox'
            checked={createPackage}
            onChange={toggle}
          />
          <span className='fw-bold'>{t('pkg_create_package', 'Create package')}</span>
          <span className='text-muted fs-7 ms-auto'>
            {t('pkg_create_package_hint', 'this product is built from your other products — price is auto-calculated')}
          </span>
        </label>

        {createPackage && (
          <div className='mt-4'>
            <div className='d-flex align-items-center justify-content-between mb-2'>
              <span className='text-muted fs-7'>
                {t('pkg_children', 'Child products')} {kindLabel && <span className='ms-1'>· {kindLabel}</span>}
              </span>
              <button type='button' className='btn btn-sm btn-light-primary' onClick={addRow}>
                + {t('pkg_add_child', 'Add child')}
              </button>
            </div>

            {children.map((child, i) => (
              <div key={i} className='d-flex align-items-start gap-2 mb-2'>
                <div className='flex-grow-1'>
                  <input
                    type='text'
                    className='form-control form-control-sm'
                    placeholder={t('pkg_child_barcode', 'child barcode')}
                    value={child.barcode}
                    onChange={(e) => setRow(i, {barcode: e.target.value, valid: undefined, name: undefined})}
                    onBlur={(e) => resolveBarcode(i, e.target.value)}
                  />
                  {checking[i] && <div className='text-muted fs-8 mt-1'>{t('pkg_checking', 'checking…')}</div>}
                  {!checking[i] && child.valid === true && (
                    <div className='text-success fs-8 mt-1'>✓ {child.name}</div>
                  )}
                  {!checking[i] && child.valid === false && (
                    <div className='text-danger fs-8 mt-1'>
                      ✗ {t('pkg_child_invalid', 'not one of your active products')}
                    </div>
                  )}
                  {!checking[i] && child.variants && child.variants.length > 0 && !child.product_id && (
                    <div className='mt-1'>
                      <div className='text-warning fs-8'>
                        {t('pkg_pick_variant', 'This barcode has variants — pick one:')}
                      </div>
                      <select
                        className='form-select form-select-sm mt-1'
                        defaultValue=''
                        onChange={(e) => {
                          const v = child.variants!.find((x) => String(x.product_id) === e.target.value)
                          if (v) pickVariant(i, v)
                        }}
                      >
                        <option value='' disabled>
                          {t('pkg_select_variant', 'Select variant')}
                        </option>
                        {child.variants.map((v) => (
                          <option key={v.product_id} value={v.product_id}>
                            {v.label} — {v.price.toFixed(2)}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
                <input
                  type='number'
                  min={1}
                  step={1}
                  className='form-control form-control-sm'
                  style={{width: 80}}
                  value={child.quantity}
                  onChange={(e) => setRow(i, {quantity: Math.max(1, parseInt(e.target.value) || 1)})}
                />
                <button
                  type='button'
                  className='btn btn-sm btn-icon btn-light-danger'
                  onClick={() => removeRow(i)}
                  aria-label='remove'
                >
                  ×
                </button>
              </div>
            ))}

            {base != null && effective != null && (
              <div className='mt-3'>
                <span className='fw-bold fs-5'>
                  {t('pkg_price', 'Package price')}: {effective.toFixed(2)}
                </span>
              </div>
            )}

            <p className='text-muted fs-8 mt-2 mb-0'>
              {t(
                'pkg_price_auto_note',
                'The price is calculated from your products — you don’t set it.'
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default PackageSection
